/**
 * OfferPopup — the player-facing offer surface (economy spec §8).
 *
 * An offer card is ONE baked image (the price/CTA is painted into the art); the
 * player just taps the image to act. This overlay shows the live set one card at
 * a time: free + video offers first, then the single random paid offer. An X
 * skips to the next; when the queue empties the popup closes.
 *
 * Actions by kind:
 *   paid  → onPaid(offer)  (triggers the IAP; its reward flyout runs elsewhere)
 *   free  → onClaim(offer) (server grants; result arrives via notifyClaimResult)
 *   video → watch offer.video.ads rewarded ads, then onClaim(offer, adsWatched)
 *
 * Body-level singleton with injected CSS, mirroring ComingSoonOverlay.
 */

import type { OfferWire, OfferClaimResult } from '@sdl/shared';
import type { AdProvider } from '../../iap/AdProvider';
import { isOfferHidden, setOfferHidden } from '../../services/offerPrefs';

const STYLE_ID = 'sdl-offerpopup-styles';

/** Emote inventory + emoji art are not built yet (spec §8). Flip on once they
 *  exist to surface the "Try emojis" button for offers that grant emoji. */
const EMOJI_TRY_ENABLED = false;

export interface OfferPopupDeps {
  /** Fire the IAP purchase for a paid offer. */
  onPaid: (offer: OfferWire) => void;
  /** Claim a free / video offer (adsWatched set for video). */
  onClaim: (offer: OfferWire, adsWatched?: number) => void;
  /** Open the local "try the dice" demo for an offer's dice. */
  onTryDice: (offer: OfferWire) => void;
  /** Localized store price for a paid SKU (null on web / before load → fallback). */
  getPrice: (sku: string) => string | null;
  /** Whether real purchases are possible here (false in the browser preview). */
  canPurchase: () => boolean;
  /** Called when BUY is tapped but purchases aren't available (browser). */
  onPurchaseUnavailable: (offer: OfferWire) => void;
  adProvider: AdProvider;
}

export class OfferPopup {
  public element: HTMLElement;
  private cardImg: HTMLImageElement;
  private progressEl: HTMLElement;
  private closeBtn: HTMLButtonElement;
  private tryDiceBtn: HTMLButtonElement;
  private tryEmojiBtn: HTMLButtonElement;
  private ctaBtn: HTMLButtonElement;
  private dontShowRow: HTMLLabelElement;
  private dontShowCheck: HTMLInputElement;
  private deps: OfferPopupDeps;

  private queue: OfferWire[] = [];
  private index = 0;
  private busy = false;
  /** True while a paid purchase started from this popup is in flight (keeps the
   *  popup open + locked until the reward choreography closes it). */
  private purchasing = false;
  private pendingClaimId: string | null = null;

  constructor(deps: OfferPopupDeps) {
    this.deps = deps;
    OfferPopup.injectStyles();
    this.element = document.createElement('div');
    this.element.className = 'sdl-offerpopup-host';
    this.element.innerHTML = `
      <div class="sdl-offerpopup-stage" role="dialog" aria-modal="true">
        <div class="sdl-offerpopup-cardwrap">
          <button class="sdl-offerpopup-close" aria-label="Close">&times;</button>
          <img class="sdl-offerpopup-card" alt="Offer" />
          <div class="sdl-offerpopup-progress"></div>
        </div>
        <div class="sdl-offerpopup-actions">
          <button class="sdl-offerpopup-try" data-try="dice">Try dice</button>
          <button class="sdl-offerpopup-try" data-try="emoji">Try emojis</button>
          <button class="sdl-offerpopup-cta" data-cta>BUY</button>
        </div>
      </div>
      <label class="sdl-offerpopup-dontshow">
        <input type="checkbox" />
        <span>do not show this offer</span>
      </label>
    `;
    document.body.appendChild(this.element);

    this.cardImg = this.element.querySelector('.sdl-offerpopup-card') as HTMLImageElement;
    this.progressEl = this.element.querySelector('.sdl-offerpopup-progress') as HTMLElement;
    this.closeBtn = this.element.querySelector('.sdl-offerpopup-close') as HTMLButtonElement;
    this.tryDiceBtn = this.element.querySelector('[data-try="dice"]') as HTMLButtonElement;
    this.tryEmojiBtn = this.element.querySelector('[data-try="emoji"]') as HTMLButtonElement;
    this.ctaBtn = this.element.querySelector('[data-cta]') as HTMLButtonElement;
    this.dontShowRow = this.element.querySelector('.sdl-offerpopup-dontshow') as HTMLLabelElement;
    this.dontShowCheck = this.dontShowRow.querySelector('input') as HTMLInputElement;

    // The card art is NOT a tap target — only the explicit CTA buys/claims, so a
    // stray tap on the poster can never trigger a purchase.
    this.ctaBtn.addEventListener('click', () => this.act());
    this.closeBtn.addEventListener('click', () => this.advance());
    this.tryDiceBtn.addEventListener('click', () => {
      const offer = this.current();
      if (offer?.dice?.length) this.deps.onTryDice(offer);
    });
    this.tryEmojiBtn.addEventListener('click', () => {
      // Reserved — emote inventory not built yet (EMOJI_TRY_ENABLED).
    });
    this.dontShowCheck.addEventListener('change', () => {
      const offer = this.current();
      if (offer) setOfferHidden(offer.id, this.dontShowCheck.checked);
    });
    this.element.addEventListener('click', (e) => {
      if (e.target === this.element) this.advance(); // backdrop tap = skip
    });
  }

  /** Show the given offers in order. No-op if empty or already showing. */
  show(offers: OfferWire[]): void {
    const renderable = offers.filter((o) => !!o.img);
    if (renderable.length === 0 || this.isVisible()) return;
    this.queue = renderable;
    this.index = 0;
    this.element.classList.add('visible');
    this.displayCurrent();
  }

  isVisible(): boolean {
    return this.element.classList.contains('visible');
  }

  private current(): OfferWire | undefined {
    return this.queue[this.index];
  }

  private displayCurrent(): void {
    const offer = this.current();
    if (!offer) {
      this.hide();
      return;
    }
    this.busy = false;
    this.pendingClaimId = null;
    this.setProgress('');
    this.cardImg.classList.remove('busy');
    this.cardImg.src = offer.img;

    // Try buttons: dice when the offer grants dice; emoji gated until emote art.
    const hasDice = !!offer.dice?.length;
    const hasEmoji = EMOJI_TRY_ENABLED && !!offer.emoji?.length;
    this.tryDiceBtn.style.display = hasDice ? '' : 'none';
    this.tryEmojiBtn.style.display = hasEmoji ? '' : 'none';

    // Primary CTA: localized price for paid, claim/watch for free/video.
    this.ctaBtn.textContent = this.ctaLabel(offer);
    this.ctaBtn.disabled = false;

    // "Do not show" reflects the saved preference for this offer.
    this.dontShowCheck.checked = isOfferHidden(offer.id);
  }

  /** CTA copy for the current offer (no connector dashes — player-facing). */
  private ctaLabel(offer: OfferWire): string {
    if (offer.kind === 'free') return 'CLAIM';
    if (offer.kind === 'video') {
      const ads = offer.video?.ads ?? 0;
      return ads > 1 ? `WATCH ${ads} ADS` : 'WATCH AD';
    }
    // Prefer the store's localized price; fall back to the server's display
    // price so a number is ALWAYS shown (the price isn't baked into the art).
    const localized = offer.storeid ? this.deps.getPrice(offer.storeid) : null;
    const price = localized ?? offer.price ?? null;
    return price ? `BUY ${price}` : 'BUY';
  }

  /** Refresh just the CTA price — used when the native store finishes loading so
   *  it upgrades from the USD fallback to the localized price. Side-effect free
   *  (won't disturb an in-flight claim/watch). */
  refresh(): void {
    const offer = this.current();
    if (offer && this.isVisible() && !this.busy) this.ctaBtn.textContent = this.ctaLabel(offer);
  }

  /** Hide the popup without losing its queue (the try-dice scene takes over the
   *  screen). Pair with resume(). */
  suspend(): void {
    this.element.classList.remove('visible');
  }

  /** Re-show the suspended popup on the same offer. */
  resume(): void {
    if (this.queue.length === 0) return;
    this.element.classList.add('visible');
    this.displayCurrent();
  }

  private advance(): void {
    if (this.busy) return;
    this.index += 1;
    if (this.index >= this.queue.length) this.hide();
    else this.displayCurrent();
  }

  private hide(): void {
    this.element.classList.remove('visible');
    this.queue = [];
    this.index = 0;
    this.busy = false;
    this.pendingClaimId = null;
  }

  private setProgress(text: string): void {
    this.progressEl.textContent = text;
    this.progressEl.classList.toggle('show', !!text);
  }

  private async act(): Promise<void> {
    const offer = this.current();
    if (!offer || this.busy) return;

    if (offer.kind === 'paid') {
      // No store in the browser preview → show the notice instead of purchasing.
      if (!this.deps.canPurchase()) {
        this.deps.onPurchaseUnavailable(offer);
        return;
      }
      // Keep the popup open through the purchase. The reward choreography (profile
      // card drop + money flyout + dice unlock) runs in main.ts and then closes
      // us via endPurchase(true); a cancel/fail re-enables via endPurchase(false).
      this.beginPurchase();
      this.deps.onPaid(offer);
      return;
    }

    if (offer.kind === 'free') {
      this.busy = true;
      this.cardImg.classList.add('busy');
      this.ctaBtn.disabled = true;
      this.pendingClaimId = offer.id;
      this.deps.onClaim(offer);
      return;
    }

    if (offer.kind === 'video') {
      const ads = offer.video?.ads ?? 0;
      this.busy = true;
      this.cardImg.classList.add('busy');
      this.ctaBtn.disabled = true;
      let watched = 0;
      for (let i = 0; i < ads; i++) {
        this.setProgress(`Watching ${i + 1} / ${ads}...`);
        const ok = await this.deps.adProvider.watchRewarded();
        if (!ok) break;
        watched += 1;
      }
      if (watched < ads) {
        // Bailed early — no reward; let them retry or skip.
        this.busy = false;
        this.cardImg.classList.remove('busy');
        this.ctaBtn.disabled = false;
        this.setProgress('Ad skipped');
        return;
      }
      this.setProgress('Collecting...');
      this.pendingClaimId = offer.id;
      this.deps.onClaim(offer, watched);
    }
  }

  /** Enter the purchasing state: lock the popup open + dim the card. */
  private beginPurchase(): void {
    this.purchasing = true;
    this.busy = true;
    this.ctaBtn.disabled = true;
    this.cardImg.classList.add('busy');
  }

  /** Resolve a paid purchase started from this popup. success → close; otherwise
   *  re-enable so the player can retry or skip. No-op if none in flight. */
  endPurchase(success: boolean): void {
    if (!this.purchasing) return;
    this.purchasing = false;
    this.busy = false;
    if (success) {
      this.hide();
    } else {
      this.cardImg.classList.remove('busy');
      this.displayCurrent();
    }
  }

  /** Feed a server claim result back in; advances on success. */
  notifyClaimResult(result: OfferClaimResult): void {
    if (!this.pendingClaimId || result.offerId !== this.pendingClaimId) return;
    this.busy = false;
    this.cardImg.classList.remove('busy');
    this.ctaBtn.disabled = false;
    this.pendingClaimId = null;
    if (result.ok) {
      this.advance();
    } else {
      this.setProgress(claimError(result.reason));
    }
  }

  destroy(): void {
    this.element.remove();
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-offerpopup-host {
        position: fixed; inset: 0; z-index: 220;
        display: none; align-items: center; justify-content: center;
        padding: 24px;
        background: rgba(4, 4, 8, 0.82);
        backdrop-filter: blur(7px); -webkit-backdrop-filter: blur(7px);
      }
      .sdl-offerpopup-host.visible { display: flex; }

      .sdl-offerpopup-stage {
        position: relative;
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        gap: 12px;
        max-width: min(92vw, 460px);
        max-height: 92vh;
        animation: sdl-offerpopup-in 0.28s cubic-bezier(0.34, 1.4, 0.6, 1) both;
      }
      @keyframes sdl-offerpopup-in {
        from { transform: translateY(20px) scale(0.92); opacity: 0; }
        to   { transform: translateY(0) scale(1); opacity: 1; }
      }

      .sdl-offerpopup-cardwrap { position: relative; display: flex; }

      .sdl-offerpopup-card {
        display: block;
        max-width: 100%; max-height: 74vh;
        width: auto; height: auto;
        border-radius: 14px;
        filter: drop-shadow(0 24px 60px rgba(0, 0, 0, 0.7));
        -webkit-tap-highlight-color: transparent;
        transition: filter 0.2s ease;
      }
      .sdl-offerpopup-card.busy { filter: drop-shadow(0 24px 60px rgba(0, 0, 0, 0.7)) brightness(0.6); pointer-events: none; }

      .sdl-offerpopup-close {
        position: absolute; top: -14px; right: -14px;
        width: 36px; height: 36px;
        background: rgba(20, 20, 26, 0.95);
        border: 1px solid rgba(255, 255, 255, 0.18);
        border-radius: 50%;
        color: rgba(255, 255, 255, 0.75);
        font-size: 24px; line-height: 1;
        cursor: pointer; z-index: 1;
        -webkit-tap-highlight-color: transparent;
        transition: background 0.2s, color 0.2s, transform 0.12s;
      }
      .sdl-offerpopup-close:active { transform: scale(0.92); background: rgba(255, 255, 255, 0.15); color: #fff; }

      .sdl-offerpopup-progress {
        position: absolute; left: 50%; bottom: 18px;
        transform: translateX(-50%);
        display: none;
        padding: 8px 18px;
        background: rgba(8, 8, 12, 0.92);
        border: 1px solid rgba(255, 255, 255, 0.16);
        border-radius: 999px;
        font-family: 'Rajdhani', sans-serif;
        font-size: 14px; font-weight: 600; letter-spacing: 0.5px;
        color: #fff;
      }
      .sdl-offerpopup-progress.show { display: block; }

      .sdl-offerpopup-actions {
        display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;
      }
      .sdl-offerpopup-try {
        padding: 10px 22px;
        background: linear-gradient(180deg, #ffe169, #f0b400);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: 999px;
        color: #1a1208;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 19px; letter-spacing: 1px;
        cursor: pointer; -webkit-tap-highlight-color: transparent;
        box-shadow: 0 6px 18px rgba(240, 180, 0, 0.4);
        transition: transform 0.12s ease, filter 0.15s ease;
      }
      .sdl-offerpopup-try:active { transform: scale(0.95); filter: brightness(1.08); }

      /* Primary CTA: the real (localized) purchase / claim action. */
      .sdl-offerpopup-cta {
        padding: 11px 26px;
        background: linear-gradient(180deg, #2bff9a, #00c46a);
        border: 1px solid rgba(255, 255, 255, 0.4);
        border-radius: 999px;
        color: #04130b;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 21px; letter-spacing: 1px;
        cursor: pointer; -webkit-tap-highlight-color: transparent;
        box-shadow: 0 6px 20px rgba(0, 196, 106, 0.45);
        transition: transform 0.12s ease, filter 0.15s ease;
      }
      .sdl-offerpopup-cta:active { transform: scale(0.95); filter: brightness(1.08); }
      .sdl-offerpopup-cta:disabled { opacity: 0.55; pointer-events: none; }

      .sdl-offerpopup-dontshow {
        position: fixed; left: 50%;
        bottom: calc(20px + env(safe-area-inset-bottom, 0px));
        transform: translateX(-50%);
        display: flex; align-items: center; gap: 8px;
        cursor: pointer; -webkit-tap-highlight-color: transparent;
        font-family: 'Rajdhani', sans-serif; font-weight: 600;
        font-size: 13px; letter-spacing: 0.4px;
        color: rgba(255, 255, 255, 0.72);
      }
      .sdl-offerpopup-dontshow input {
        width: 18px; height: 18px;
        accent-color: var(--gold, #FFD700);
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }
}

function claimError(reason?: string): string {
  switch (reason) {
    case 'already_claimed': return 'Already claimed';
    case 'cooldown': return 'Come back later';
    case 'ineligible': return 'Not available';
    default: return 'Could not claim';
  }
}
